import React, { useState } from "react";
import { Alert, Button, Card, Space, Typography } from "antd";
import { KeyOutlined, LogoutOutlined, SafetyOutlined } from "@ant-design/icons";
import { isRecoverySession, useAuthSession } from "../../../lib/http/session";
import { strongMethods } from "../../../lib/http/stepup";
import { isPasskeySupported } from "../../../lib/webauthn/webauthn";
import { signOut } from "../../../utils/auth/sessionUtilities";
import { pageNavigator } from "../../../utils/navigation/pageNavigator";
import BrandLogo from "../../../components/branding/BrandLogo";
import StepUpForm from "../../../components/auth/mfa/StepUpForm";
import TotpSetup from "./TotpSetup";
import PasskeySetup from "./PasskeySetup";
import "./Security.css";

const { Title, Paragraph } = Typography;

type Choice = "totp" | "passkey" | null;

// The only screen a confined session can use (contract §4.7, §6.10): enrol a factor, or step up with an enrolled one, then the app loads.
const EnrolmentGate: React.FC = () => {
	const { info } = useAuthSession();
	const [choice, setChoice] = useState<Choice>(null);
	const recovery = isRecoverySession(info);
	const enrolledMethods = strongMethods(info?.mfa?.methods);
	// A recovery session reports the lost factor as enrolled; it must add a new one, never step up with the old (S32).
	const confirmOnly = !recovery && Boolean(info?.mfa?.enrolled) && enrolledMethods.length > 0;
	const requireEmailCode = !recovery && info?.session?.audience === "staff";

	// A full load boots the app against the lifted session; were it somehow still confined, the guards bring it back here.
	const finished = () => pageNavigator.go("/");

	const leave = () => {
		void signOut().finally(() => pageNavigator.go("/login"));
	};

	const title = confirmOnly ? "Confirm it's you" : "Set up two-step verification";

	return (
		<div className="security-gate" data-testid="security-gate">
			<Card className="security-gate-card" bordered={false}>
				<div className="security-gate-logo">
					<BrandLogo height={36} />
				</div>
				<Title level={3} className="security-gate-title">
					{title}
				</Title>

				{confirmOnly ? (
					<StepUpForm
						methods={enrolledMethods}
						onSuccess={finished}
						submitLabel="Continue"
						intro={<Paragraph type="secondary">Your account now needs two-step verification. Confirm with your authenticator app or passkey to continue.</Paragraph>}
					/>
				) : (
					<>
						<Paragraph type="secondary" data-testid="security-gate-intro">
							{recovery
								? "You signed in with a recovery method. Add a new authenticator app or passkey to continue. You can remove the lost one afterwards in Security settings."
								: "Your account needs an authenticator app or a passkey as a second step at sign-in. Choose one to continue."}
						</Paragraph>
						{recovery && (
							<Alert type="info" showIcon message="You have 10 minutes to add a new method before you need to sign in again." style={{ marginBottom: "var(--mg-sp-6)" }} />
						)}
						{choice === null && (
							<Space direction="vertical" style={{ width: "100%" }} size="middle">
								<Button data-testid="security-gate-totp" block size="large" icon={<SafetyOutlined />} onClick={() => setChoice("totp")}>
									Authenticator app
								</Button>
								{isPasskeySupported() && (
									<Button data-testid="security-gate-passkey" block size="large" icon={<KeyOutlined />} onClick={() => setChoice("passkey")}>
										Passkey on this device
									</Button>
								)}
							</Space>
						)}
						{choice === "totp" && <TotpSetup requireEmailCode={requireEmailCode} onDone={finished} onCancel={() => setChoice(null)} />}
						{choice === "passkey" && <PasskeySetup requireEmailCode={requireEmailCode} onDone={finished} onCancel={() => setChoice(null)} />}
					</>
				)}

				<div className="security-gate-footer">
					<Button data-testid="security-gate-signout" type="link" icon={<LogoutOutlined />} onClick={leave}>
						Sign out
					</Button>
				</div>
			</Card>
		</div>
	);
};

export default EnrolmentGate;
